import {
  type PhoneVerificationAcceptedResponse,
  type PhoneVerificationCheckCompletedSseEvent,
  type PhoneVerificationResendCompletedSseEvent,
  type PhoneVerificationResendFailedSseEvent,
  type TwilioVerificationStatus,
  AsyncRequestStatus,
  PHONE_VERIFICATION_RESEND_FINAL_EVENTS,
  SseEvent,
} from '@sparrow-card/sparrow-card-constants';
import {
  ensureApplicationSseSession,
  waitForApplicationSseEvent,
} from 'utils/sse/applicationSseSession';
import { SseError } from 'utils/sse/SseError';
import Api, { FetchMethodType } from './Api';

export interface TwilioResendVerificationRequestData {
  applicationId: string;
  submissionId: string;
}

export interface TwilioResendVerificationResponseData {
  status: TwilioVerificationStatus;
  resendAttemptsLeft?: number;
}

export interface TwilioVerifyCodeRequestData {
  applicationId: string;
  submissionId: string;
  verificationCode: string;
}

export interface TwilioResponse {
  status: TwilioVerificationStatus;
}

const isResendCompletedSseEvent = (sseEvent: {
  event: string;
}): sseEvent is PhoneVerificationResendCompletedSseEvent =>
  sseEvent.event === SseEvent.PhoneVerificationResendCompleted;

const isResendFailedSseEvent = (sseEvent: {
  event: string;
}): sseEvent is PhoneVerificationResendFailedSseEvent =>
  sseEvent.event === SseEvent.PhoneVerificationResendFailed;

const isCheckCompletedSseEvent = (sseEvent: {
  event: string;
}): sseEvent is PhoneVerificationCheckCompletedSseEvent =>
  sseEvent.event === SseEvent.PhoneVerificationCheckCompleted;

export default class TwilioApi extends Api {
  public async resendVerification({
    applicationId,
    submissionId,
  }: TwilioResendVerificationRequestData) {
    ensureApplicationSseSession(submissionId);

    const accepted = await this.fetch<PhoneVerificationAcceptedResponse>(
      `/twilio/resend-verification/${applicationId}`,
      {
        method: FetchMethodType.POST,
        body: { submissionId },
      },
    );

    if (accepted.status !== AsyncRequestStatus.Accepted) {
      throw new Error(`Unexpected resend verification response for submissionId: ${submissionId}`);
    }

    const sseEvent = await waitForApplicationSseEvent({
      resolveEvents: [...PHONE_VERIFICATION_RESEND_FINAL_EVENTS],
      closeOnEvents: [],
    });

    if (isResendFailedSseEvent(sseEvent)) {
      throw new SseError(`${sseEvent.payload.error} (submissionId: ${submissionId})`);
    }

    if (isResendCompletedSseEvent(sseEvent)) {
      return sseEvent.payload.result;
    }

    throw new SseError(`unexpected event payload for submissionId: ${submissionId}`);
  }

  public async verifyCode({
    applicationId,
    submissionId,
    verificationCode,
  }: TwilioVerifyCodeRequestData) {
    ensureApplicationSseSession(submissionId);

    const accepted = await this.fetch<PhoneVerificationAcceptedResponse>(
      `/twilio/check-verification/${applicationId}`,
      {
        method: FetchMethodType.POST,
        body: {
          submissionId,
          verificationCode,
        },
      },
    );

    if (accepted.status !== AsyncRequestStatus.Accepted) {
      throw new Error(`Unexpected check verification response for submissionId: ${submissionId}`);
    }

    const sseEvent = await waitForApplicationSseEvent({
      resolveEvents: [SseEvent.PhoneVerificationCheckCompleted],
      closeOnEvents: [],
    });

    if (isCheckCompletedSseEvent(sseEvent)) {
      return sseEvent.payload.result;
    }

    throw new SseError(`unexpected event payload for submissionId: ${submissionId}`);
  }
}
